import { useEffect, useState } from 'react'
import {
	ArrowDownTrayIcon,
	ChartBarIcon,
	CheckIcon,
	ClipboardDocumentListIcon,
	DocumentTextIcon,
	PhoneIcon,
	PlayIcon,
} from '@heroicons/react/24/outline'
import moment from 'moment'
import { io } from 'socket.io-client'
import { useTranslation } from 'react-i18next'
import CustomerService from '@/services/CustomerService'
import { useNotifications } from '@/hooks/useNotifications'

export default function DialerContactRecord({ dialerDetails }) {
	const { t } = useTranslation()
	const [callSummary, setCallSummary] = useState(dialerDetails?.callSummary || '')
	const [savingSummary, setSavingSummary] = useState(false)
	const [message, setMessage] = useState(null)
	const [error, setError] = useState(null)
	const [callingWithAnalysis, setCallingWithAnalysis] = useState(false)
	const [transcript, setTranscript] = useState('')

	useNotifications({
		error,
		message,
		clearError: () => setError(null),
		clearMessage: () => setMessage(null),
	})

	useEffect(() => {
		setCallSummary(dialerDetails?.callSummary || '')
	}, [dialerDetails?.callSummary])

	useEffect(() => {
		const apiUrl = window.RUNTIME_CONFIG?.API_URL || 'http://localhost:5001'
		const socket = io(apiUrl, {
			transports: ['websocket', 'polling'],
			autoConnect: true,
		})

		socket.onAny((eventName, ...args) => {
			if (eventName !== 'transcript') return
			const nextTranscript = args?.[0]?.transcript || ''
			if (!nextTranscript) return

			setTranscript(currentTranscript => `${currentTranscript}${nextTranscript}\n\n`)
		})

		return () => {
			socket.disconnect()
		}
	}, [])

	const phoneNumber = dialerDetails?.representativePhone || ''
	const callUrl = phoneNumber ? `tel:${phoneNumber}` : '#'

	const handleCall = () => {
		if (phoneNumber) {
			window.location.href = callUrl
		}
	}

	const handleCallWithAnalysis = async () => {
		if (!phoneNumber || callingWithAnalysis) return

		setCallingWithAnalysis(true)
		setError(null)
		setMessage(null)

		const { error: callError } = await CustomerService.callWithAnalysis(phoneNumber, '')
		setCallingWithAnalysis(false)

		if (callError) {
			setError(callError)
			return
		}

		setMessage(t('dialer.actions.callStarted'))
	}

	const handleSaveSummary = async () => {
		setSavingSummary(true)
		setError(null)
		setMessage(null)
		setMessage(t('dialer.summary.savedSuccess'))
		setSavingSummary(false)
	}

	return (
		<div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
			<div className="col-span-1 xl:col-span-2">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm">
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<PhoneIcon className="h-5 w-5 text-sky-700" />
							<span>{t('dialer.actions.title')}</span>
						</h3>
					</div>

					<div className="px-6 py-6">
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<button
								type="button"
								onClick={handleCall}
								disabled={!phoneNumber}
								className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-md shadow-sm transition ${
									phoneNumber
										? 'bg-sky-600 text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2'
										: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								<PhoneIcon className="h-5 w-5" />
								<span>{t('dialer.actions.call')}</span>
							</button>

							<button
								type="button"
								onClick={handleCallWithAnalysis}
								disabled={!phoneNumber || callingWithAnalysis}
								className={`inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-md shadow-sm transition ${
									phoneNumber
										? 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
										: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								<ChartBarIcon className="h-5 w-5" />
								<span>{callingWithAnalysis ? t('dialer.actions.calling') : t('dialer.actions.callWithAnalysis')}</span>
							</button>

						</div>
					</div>
				</div>
			</div>

			<div className="col-span-1 xl:col-span-2">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm">
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<ClipboardDocumentListIcon className="h-5 w-5 text-sky-700" />
							<span>{t('dialer.summary.title')}</span>
						</h3>
					</div>

					<div className="px-6 py-4">
						<div className="space-y-3">
							<textarea
								value={callSummary}
								onChange={(e) => setCallSummary(e.target.value)}
								placeholder={t('dialer.summary.placeholder')}
								disabled={savingSummary}
								rows={6}
								className="block w-full resize-none rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
							/>
							<div className="flex items-center gap-2 justify-end">
								<button
									type="button"
									onClick={handleSaveSummary}
									disabled={savingSummary}
									className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
								>
									<CheckIcon className="h-4 w-4" />
									{savingSummary ? t('dialer.summary.saving') : t('dialer.summary.save')}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="col-span-1">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm h-full flex flex-col">
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<DocumentTextIcon className="h-5 w-5 text-sky-700" />
							<span>{t('dialer.transcript.title')}</span>
						</h3>
					</div>

					<div className="flex-1 px-6 py-4 overflow-y-auto">
						<div className="space-y-4">
							<div className="text-sm text-gray-600">
								<p className="text-gray-700 whitespace-pre-wrap">
									{transcript || t('dialer.transcript.empty')}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="col-span-1">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm h-full flex flex-col">
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<ChartBarIcon className="h-5 w-5 text-sky-700" />
							<span>{t('dialer.analysis.title')}</span>
						</h3>
					</div>

					<div className="flex-1 px-6 py-4 overflow-y-auto">
						<div className="space-y-4">
							<div className="text-sm text-gray-600">
								<p className="mb-2">
									<span className="font-semibold text-gray-900">{t('dialer.analysis.lastAnalyzed')}</span>{' '}
									{moment().format('YYYY-MM-DD HH:mm')}
								</p>
								<div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
									<p className="text-gray-700 whitespace-pre-wrap">
										{t('dialer.analysis.empty')}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="col-span-1">
				<div className="rounded-md border border-slate-200 bg-white shadow-sm h-full flex flex-col">
					<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
						<h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
							<PlayIcon className="h-5 w-5 text-sky-700" />
							<span>{t('dialer.recording.title')}</span>
						</h3>
					</div>

					<div className="flex-1 px-6 py-4 overflow-y-auto">
						<div className="space-y-4">
							<div className="text-sm text-gray-600">
								<p className="mb-4 text-gray-700">
									{t('dialer.recording.description')}
								</p>
							</div>
							<button
								type="button"
								disabled
								className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md shadow-sm transition bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 opacity-75 cursor-not-allowed"
							>
								<ArrowDownTrayIcon className="h-4 w-4" />
								{t('dialer.recording.download')}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
